import React, { useEffect, useMemo, useState } from 'react'
import {
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Box,
    Text,
    ModalCloseButton,
    Spinner,
    Center,
} from '@chakra-ui/react'
import { replaceTemplateVariables } from '~utils/replace-template-variables'
import {
    ChatMessageFieldsFragment,
    NotificationLogMessageFieldsFragment,
    useNotificationTemplateLazyQuery,
    useScheduledBulkMessageLazyQuery,
} from '~graphql/generated/graphql'
import SharedLabel from '~components/shared/form/shared-label'
import SharedStack from '~components/shared/wrappers/shared-stack'
import {
    hasEmailContent,
    NotificationPreviewRef,
    resolveNotificationPreviewContent,
} from './resolve-preview-content'

interface EmailPreviewButtonProps {
    message: ChatMessageFieldsFragment | NotificationLogMessageFieldsFragment
    notification: NotificationPreviewRef
    isBulkMessage?: boolean
}

export default function EmailPreviewButton({
    message,
    notification,
    isBulkMessage = false,
}: EmailPreviewButtonProps) {
    const {
        isOpen: isPreviewOpen,
        onOpen: onPreviewOpen,
        onClose: onPreviewClose,
    } = useDisclosure()
    const [resolvedNotification, setResolvedNotification] =
        useState(notification)

    const [fetchTemplate, { loading: loadingTemplate }] =
        useNotificationTemplateLazyQuery()
    const [fetchBulkMessage, { loading: loadingBulk }] =
        useScheduledBulkMessageLazyQuery()

    useEffect(() => {
        setResolvedNotification(notification)
    }, [notification])

    useEffect(() => {
        if (!isPreviewOpen) return
        if (hasEmailContent(notification)) return

        if (isBulkMessage) {
            void fetchBulkMessage({
                variables: {
                    scheduledBulkMessageId: notification.id,
                },
            }).then(result => {
                const full = result.data?.scheduledBulkMessage
                if (!full) return
                setResolvedNotification({
                    id: full.id,
                    name: full.name,
                    subject: full.subject,
                    body: full.body,
                    email_body: full.email_body,
                })
            })
            return
        }

        void fetchTemplate({
            variables: {
                notificationTemplateId: notification.id,
            },
        }).then(result => {
            const full = result.data?.notificationTemplate
            if (!full) return
            setResolvedNotification(
                resolveNotificationPreviewContent(notification, [full]),
            )
        })
    }, [
        isPreviewOpen,
        notification,
        isBulkMessage,
        fetchBulkMessage,
        fetchTemplate,
    ])

    const subject = useMemo(
        () =>
            replaceTemplateVariables(
                resolvedNotification.subject,
                message.template_data,
            ).value,
        [resolvedNotification.subject, message.template_data],
    )

    const emailBody = useMemo(
        () =>
            replaceTemplateVariables(
                resolvedNotification.email_body || resolvedNotification.body,
                message.template_data,
            ).value,
        [
            resolvedNotification.email_body,
            resolvedNotification.body,
            message.template_data,
        ],
    )

    const loadingContent =
        (loadingTemplate || loadingBulk) && !hasEmailContent(resolvedNotification)

    return (
        <React.Fragment>
            <Text
                fontWeight="semibold"
                textDecor="underline"
                cursor="pointer"
                onClick={onPreviewOpen}
            >
                {resolvedNotification.name || 'Unnamed Notification'}
            </Text>

            <Modal isOpen={isPreviewOpen} onClose={onPreviewClose}>
                <ModalOverlay />
                <ModalContent w="100%" maxW="800px">
                    <ModalCloseButton />
                    <ModalHeader>Email Preview</ModalHeader>
                    <ModalBody justifyContent="center">
                        {loadingContent ? (
                            <Center py={12}>
                                <Spinner />
                            </Center>
                        ) : (
                            <>
                                <SharedStack mb={2} gap={1}>
                                    <SharedLabel mb={1}>Subject</SharedLabel>
                                    <Box p={2} bg="#f7f8fa" w="full">
                                        <Text>{subject}</Text>
                                    </Box>
                                </SharedStack>
                                <SharedLabel mb={1}>Body</SharedLabel>
                                {isPreviewOpen && (
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: emailBody,
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onPreviewClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </React.Fragment>
    )
}
